declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type EventValue = string | number | boolean | null | undefined;
type EventParams = Record<string, EventValue>;

// Must match the GA4 property linked to Google Ads (the one the marketing
// site's gtag reports to), so signup events land where conversions are read.
const DEFAULT_MEASUREMENT_ID = "G-46RSB76QET";
const measurementId =
  import.meta.env.PROD
    ? (import.meta.env.VITE_GA_MEASUREMENT_ID || DEFAULT_MEASUREMENT_ID)
    : null;

let analyticsInitialized = false;

const sanitizeParams = (params: EventParams = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );

export function initDashboardAnalytics() {
  if (analyticsInitialized || typeof window === "undefined" || !measurementId) {
    return;
  }

  analyticsInitialized = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };

  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[data-ga-measurement-id="${measurementId}"]`,
  );

  if (!existingScript) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.dataset.gaMeasurementId = measurementId;
    document.head.appendChild(script);
  }

  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });
}

const ATTRIBUTION_STORAGE_KEY = "flow_dashboard_attribution_v1";

// Captures campaign params (forwarded from the marketing site or an ad click)
// so they can be attached to the signup's onboarding formData.
export function captureDashboardAttribution() {
  if (typeof window === "undefined") {
    return;
  }

  const params = new URL(window.location.href).searchParams;
  const attribution = sanitizeParams({
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_term: params.get("utm_term") || undefined,
    utm_content: params.get("utm_content") || undefined,
    gclid: params.get("gclid") || undefined,
    fbclid: params.get("fbclid") || undefined,
    referrer: document.referrer || undefined,
    landing_path: window.location.pathname,
  });

  if (!attribution.utm_source && !attribution.gclid && !attribution.fbclid) {
    return;
  }

  window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  trackDashboardEvent("attribution_captured", attribution);
}

export function getStoredDashboardAttribution(): Record<string, string> | null {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return null;
  }
}

export function trackDashboardEvent(eventName: string, params: EventParams = {}) {
  if (typeof window === "undefined" || !measurementId) {
    return;
  }

  initDashboardAnalytics();
  window.gtag?.("event", eventName, sanitizeParams({ app: "dashboard", ...params }));
}

export function trackDashboardPageView(pathname = window.location.pathname, title = document.title) {
  trackDashboardEvent("page_view", {
    page_path: pathname,
    page_location: window.location.href,
    page_title: title,
  });
}
